import { FC, useState, useRef } from 'react'
import { fetchExplanation, fetchFollowUp, submitRating, ExplainPayload, MessageEntry } from 'helpers/explain'
import siteConfig from 'config'

type props = ExplainPayload & { onSuccess?: () => void }

function formatText(text: string) {
  return text
    .replace(/^#+\s+.+\n*/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br />')
    .replace(/^(<br \/>)+/, '')
    .trim()
}

const ExplainButton: FC<props> = ({ onSuccess, ...payload }) => {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [messages, setMessages] = useState<MessageEntry[]>([])
  const [conversationId, setConversationId] = useState('')
  const [promptVersion, setPromptVersion] = useState('')
  const [rating, setRating] = useState<'up' | 'down' | null>(null)
  const [followUp, setFollowUp] = useState('')
  const [followUpLoading, setFollowUpLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = async () => {
    setState('loading')
    try {
      const res = await fetchExplanation(payload)
      setMessages([
        { role: 'user', content: payload.question },
        { role: 'assistant', content: res.explanation },
      ])
      setConversationId(res.conversationId)
      setPromptVersion(res.promptVersion)
      setState('done')
      onSuccess?.()
    } catch {
      setState('error')
    }
  }

  const handleFollowUp = async () => {
    if (!followUp.trim() || followUpLoading) return
    const text = followUp.trim()
    setFollowUp('')
    setFollowUpLoading(true)

    const updatedMessages: MessageEntry[] = [...messages, { role: 'user', content: text }]
    setMessages(updatedMessages)

    try {
      const reply = await fetchFollowUp(messages, text, {
        testType: payload.testType,
        questionType: payload.questionType,
        partHtml: payload.partHtml,
      })
      setMessages([...updatedMessages, { role: 'assistant', content: reply }])
    } catch {
      setMessages([...updatedMessages, { role: 'assistant', content: '...' }])
    }
    setFollowUpLoading(false)
    inputRef.current?.focus()
  }

  const handleRate = (r: 'up' | 'down') => {
    setRating(r)
    submitRating(conversationId, r, promptVersion, payload).catch(() => {})
  }

  if (state === 'done') {
    // Show only assistant messages (skip the first user message which is the question itself)
    const assistantMessages = messages.filter(m => m.role === 'assistant')
    const userFollowUps = messages.filter(m => m.role === 'user').slice(1)

    return (
      <div className="mt-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
        {assistantMessages.map((m, i) => (
          <div key={i}>
            {userFollowUps[i - 1] && (
              <p className="text-xs text-indigo-500 mt-2 mb-1">{userFollowUps[i - 1].content}</p>
            )}
            {i > 0 && <div className="border-t border-indigo-200 my-2" />}
            <div
              className="text-sm text-slate-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatText(m.content) }}
            />
          </div>
        ))}

        {followUpLoading && (
          <>
            <div className="border-t border-indigo-200 my-2" />
            {messages[messages.length - 1]?.role === 'user' && (
              <p className="text-xs text-indigo-500 mb-1">{messages[messages.length - 1].content}</p>
            )}
            <p className="text-sm text-slate-400 animate-pulse">...</p>
          </>
        )}

        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-indigo-200">
          <input
            ref={inputRef}
            type="text"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFollowUp()}
            placeholder={siteConfig.labels.explain.followUpPlaceholder}
            disabled={followUpLoading}
            className="flex-1 text-xs bg-white border border-indigo-200 rounded-full px-3 py-1 outline-none focus:border-indigo-400 disabled:opacity-50"
          />
          <button
            onClick={() => handleRate('up')}
            className={`text-sm px-1.5 py-0.5 rounded transition-colors ${rating === 'up' ? 'bg-green-100 text-green-600' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`}
          >
            👍
          </button>
          <button
            onClick={() => handleRate('down')}
            className={`text-sm px-1.5 py-0.5 rounded transition-colors ${rating === 'down' ? 'bg-red-100 text-red-600' : 'text-slate-400 hover:text-red-600 hover:bg-red-50'}`}
          >
            👎
          </button>
        </div>
        <span className="text-[10px] text-slate-400 mt-1 block">{siteConfig.labels.explain.disclaimer}</span>
      </div>
    )
  }

  return (
    <button
      onClick={handleClick}
      disabled={state === 'loading'}
      className="mt-2 text-xs font-medium text-indigo-600 border border-indigo-300 rounded-full px-3 py-1 hover:bg-indigo-50 disabled:opacity-50 transition-colors"
    >
      {state === 'loading' ? 'Vysvetľujem…' : state === 'error' ? 'Skúsiť znova' : '✦ Vysvetliť'}
    </button>
  )
}

export default ExplainButton
